import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { formatCurrency } from '@/common/utils/formatters'
import { unFormatCurrency } from '@/common/utils/unFormatters'
import { GovernmentAllocationEntryProductCategoryValidationSchema } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/update.schema'
import { ClientCompanyAllowedCategoryAndProductData } from '@/features/privateCompanies/types/allowedCategoriesAndProducts.type'
import { CompanyGovernmentAllocationEntryProduct } from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'
import { FileBoxIcon, Trash2Icon } from 'lucide-react'
import { useFormContext, useWatch } from 'react-hook-form'

interface EntryProductFormItemProps {
  entryProductIndex: number
  entryProduct: CompanyGovernmentAllocationEntryProduct
  onDelete: () => void
  disabled: boolean
  allowedProductSummary: ClientCompanyAllowedCategoryAndProductData
  onTotalValueChange: () => void;
}

export const EntryProductFormItem = ({
  entryProductIndex,
  entryProduct,
  onDelete,
  disabled,
  allowedProductSummary,
  onTotalValueChange,
}: EntryProductFormItemProps) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<GovernmentAllocationEntryProductCategoryValidationSchema>()

  const watchCurrentProductTotalValue = useWatch({
    control,
    name: `entry_products.${entryProductIndex}.total_value`,
  });

  const availableTotal = Number(allowedProductSummary?.available_total) || 0;
  const availableForAllocation = Math.max(availableTotal - unFormatCurrency(watchCurrentProductTotalValue), 0);

  return (
    <Box className="flex flex-col gap-4 p-4">
      <div className="flex justify-between">
        <div className="flex items-center gap-x-2">
          <FileBoxIcon strokeWidth={1} />

          <p>{entryProduct?.product?.display_name}</p>
        </div>

        <Button
          type="button"
          variant="outline-error"
          className="size-10 p-0 text-danger"
          onClick={onDelete}
          disabled={disabled}
        >
          <Trash2Icon strokeWidth={1} />
        </Button>
      </div>

      <div className='flex flex-col gap-1'>
        <CurrencyTextField
          label="Valor total"
          {...register(`entry_products.${entryProductIndex}.total_value`)}
          onChange={(event) => {
            register(`entry_products.${entryProductIndex}.total_value`).onChange(event);
            onTotalValueChange();
          }}
          errorMessage={errors.entry_products?.[entryProductIndex]?.total_value?.message || undefined}
          forceValue
          value={watch(`entry_products.${entryProductIndex}.total_value`)}
          disabled={disabled}
          markAsRequired
        />

        {allowedProductSummary && (
          <div className='flex gap-3'>
            <p className='italic text-xs text-white-400'>
              Total possível: {allowedProductSummary?.available_total ? formatCurrency(Number(allowedProductSummary?.available_total) || 0) : '--'}
            </p>

            <p className='italic text-xs text-white-400'>
              Disponível para distribuir: {formatCurrency(availableForAllocation)}
            </p>

            <p className='italic text-xs text-white-400'>
              Total garantido: {allowedProductSummary?.granted_total ? formatCurrency(Number(allowedProductSummary?.granted_total) || 0) : '--'}
            </p>

            <p className='italic text-xs text-white-400'>
              Total inserido: {allowedProductSummary?.entry_total ? formatCurrency(Number(allowedProductSummary?.entry_total) || 0) : '--'}
            </p>
          </div>
        )}
      </div>
    </Box>
  )
}
